import React, { useEffect, useState, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  ArrowLeftIcon,
  CreditCardIcon,
  UserIcon,
  BanknotesIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  CheckBadgeIcon,
  IdentificationIcon,
  ShoppingBagIcon,
  CalendarIcon,
  HashtagIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';

interface PaidDrink {
  id: number;
  token: string;
  amount: number;
  event_label: string;
  event_id: number;
  transaction_id: string;
  already_paid: boolean;
  webhook_processed?: boolean; // True wenn IPN-Webhook diese Zahlung bereits verarbeitet hat
}

const PayPalSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { BASE_URL } = useContext(AuthContext);
  const [params, setParams] = useState<{ [key: string]: string }>({});
  const [paidDrinks, setPaidDrinks] = useState<PaidDrink[]>([]);
  const [backendChecked, setBackendChecked] = useState(false);

  useEffect(() => {
    // Alle URL-Parameter sammeln
    const allParams: { [key: string]: string } = {};
    searchParams.forEach((value, key) => {
      allParams[key] = value;
    });
    setParams(allParams);

    // Wenn custom und tx vorhanden sind, Backend-Bestätigung senden
    if (allParams.custom && allParams.tx) {
      confirmPaymentWithBackend(allParams);
    }
  }, [searchParams]);

  const confirmPaymentWithBackend = async (paypalParams: { [key: string]: string }) => {
    try {
      const response = await apiClient.post('/api/drinks/paypal-confirm', {
        custom: paypalParams.custom || paypalParams.cm,
        tx: paypalParams.tx || paypalParams.txn_id,
        amt: paypalParams.amt || paypalParams.mc_gross,
        st: paypalParams.st || paypalParams.payment_status,
        payment_date: paypalParams.payment_date
      });

      console.log('Zahlung erfolgreich bestätigt:', response.data);
      // Speichere die bezahlten Drinks
      if (response.data.drinks && Array.isArray(response.data.drinks)) {
        setPaidDrinks(response.data.drinks);
      }
    } catch (error: any) {
      console.error('Fehler bei Backend-Bestätigung:', error.response?.data || error.message);
    } finally {
      setBackendChecked(true);
    }
  };

  // Format currency
  const formatCurrency = (amount: string, currency: string = 'EUR') => {
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency
    }).format(num);
  };

  // Format date
  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Bestimme den Status der Zahlung
  const getPaymentStatus = () => {
    // Wenn keine Referenz vorhanden, nur PayPal-Zahlung ohne Verzehr-Verknüpfung
    if (!params.custom) {
      return 'no_reference';
    }

    // Warten auf Backend-Antwort
    if (!backendChecked) {
      return 'loading';
    }

    // Kein Verzehr gefunden
    if (paidDrinks.length === 0) {
      return 'not_found';
    }

    // Prüfe ob alle bereits bezahlt waren
    const allAlreadyPaid = paidDrinks.every(drink => drink.already_paid);
    const someAlreadyPaid = paidDrinks.some(drink => drink.already_paid);
    const noneAlreadyPaid = !someAlreadyPaid;

    if (allAlreadyPaid) {
      return 'all_already_paid';
    } else if (someAlreadyPaid) {
      return 'partially_paid';
    } else if (noneAlreadyPaid) {
      return 'newly_paid';
    }

    return 'newly_paid';
  };

  const paymentStatus = getPaymentStatus();

  // Bestimme Header basierend auf Status
  const getHeader = () => {
    switch (paymentStatus) {
      case 'newly_paid':
        return {
          icon: <CheckCircleIcon className="h-8 w-8 text-success-600 dark:text-success-400" />,
          title: 'PayPal-Zahlung erfolgreich',
          subtitle: 'Ihre Zahlung wurde bei PayPal abgeschlossen'
        };
      case 'all_already_paid':
        return {
          icon: <InformationCircleIcon className="h-8 w-8 text-info-600 dark:text-info-400" />,
          title: 'PayPal-Zahlung erfolgreich',
          subtitle: 'Ihre Zahlung wurde bei PayPal abgeschlossen'
        };
      case 'partially_paid':
        return {
          icon: <InformationCircleIcon className="h-8 w-8 text-warning-600 dark:text-warning-400" />,
          title: 'PayPal-Zahlung erfolgreich',
          subtitle: 'Ihre Zahlung wurde bei PayPal abgeschlossen'
        };
      case 'not_found':
        return {
          icon: <ExclamationTriangleIcon className="h-8 w-8 text-warning-600 dark:text-warning-400" />,
          title: 'Warnung: Kein Verzehr gefunden',
          subtitle: 'PayPal-Zahlung war erfolgreich, aber keine passenden Verzehrkosten in der Datenbank'
        };
      case 'no_reference':
        return {
          icon: <CheckCircleIcon className="h-8 w-8 text-success-600 dark:text-success-400" />,
          title: 'PayPal-Zahlung erfolgreich',
          subtitle: 'Zahlung abgeschlossen - keine Verzehrkosten-Referenz enthalten'
        };
      case 'loading':
        return {
          icon: <ClockIcon className="h-8 w-8 text-gray-600 dark:text-gray-400" />,
          title: 'Einen Moment bitte...',
          subtitle: 'Ihre Zahlung wird gerade verarbeitet und geprüft'
        };
      default:
        return {
          icon: <CheckCircleIcon className="h-8 w-8 text-success-600 dark:text-success-400" />,
          title: 'PayPal-Zahlung erfolgreich',
          subtitle: 'Ihre Zahlung wurde bei PayPal abgeschlossen'
        };
    }
  };

  const header = getHeader();

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          {header.icon}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {header.title}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {header.subtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Status-spezifische Meldungen */}
      {paymentStatus === 'newly_paid' && (
        <div className="bg-success-50 dark:bg-success-900/20 border-2 border-success-500 dark:border-success-600 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <CheckCircleIcon className="h-7 w-7 text-success-600 dark:text-success-400" />
            <p className="text-lg text-success-800 dark:text-success-200 font-bold">
              ✓ Verzehr erfolgreich bezahlt!
            </p>
          </div>
          <p className="text-sm text-success-700 dark:text-success-300 font-medium">
            Ihre Verzehrkosten wurden als "bezahlt" markiert und sind jetzt in "Meine Verzehrkosten" aktualisiert.
          </p>
        </div>
      )}

      {paymentStatus === 'all_already_paid' && (
        <div className="bg-info-50 dark:bg-info-900/20 border-2 border-info-500 dark:border-info-600 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <InformationCircleIcon className="h-7 w-7 text-info-600 dark:text-info-400" />
            <p className="text-lg text-info-800 dark:text-info-200 font-bold">
              ⚠ Achtung: Verzehr war bereits bezahlt!
            </p>
          </div>
          <p className="text-sm text-info-700 dark:text-info-300 font-medium">
            Die Verzehrkosten waren bereits <strong>vor dieser Zahlung beglichen</strong>. Möglicherweise haben Sie doppelt bezahlt - bitte kontaktieren Sie den Vorstand.
          </p>
        </div>
      )}

      {paymentStatus === 'partially_paid' && (
        <div className="bg-warning-50 dark:bg-warning-900/20 border-2 border-warning-500 dark:border-warning-600 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <InformationCircleIcon className="h-7 w-7 text-warning-600 dark:text-warning-400" />
            <p className="text-lg text-warning-800 dark:text-warning-200 font-bold">
              ⚠ Hinweis: Teilweise bereits bezahlt
            </p>
          </div>
          <p className="text-sm text-warning-700 dark:text-warning-300 font-medium">
            Einige Verzehrkosten waren bereits <strong>vor dieser Zahlung beglichen</strong>. Details sehen Sie unten.
          </p>
        </div>
      )}

      {/* Verzehrkosten-Liste */}
      {paidDrinks.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <ShoppingBagIcon className="h-6 w-6 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {paymentStatus === 'newly_paid' ? 'Bezahlte Verzehrkosten' : 'Verzehrkosten-Details'}
            </h2>
          </div>
          <div className="space-y-3">
            {paidDrinks.map((drink, index) => (
              <div
                key={drink.id || index}
                className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                  drink.already_paid
                    ? 'bg-info-50 dark:bg-info-900/20 border-info-400 dark:border-info-600'
                    : 'bg-success-50 dark:bg-success-900/20 border-success-400 dark:border-success-600'
                }`}
              >
                <div className="flex items-center gap-3">
                  {drink.already_paid ? (
                    <InformationCircleIcon className="h-6 w-6 text-info-600 dark:text-info-400 flex-shrink-0" />
                  ) : (
                    <CheckCircleIcon className="h-6 w-6 text-success-600 dark:text-success-400 flex-shrink-0" />
                  )}
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {drink.event_label}
                    </p>
                    <p className={`text-sm font-bold ${
                      drink.already_paid
                        ? 'text-info-700 dark:text-info-300'
                        : 'text-success-700 dark:text-success-300'
                    }`}>
                      {drink.already_paid ? '⚠ WAR BEREITS BEZAHLT' : '✓ NEU BEZAHLT'}
                    </p>
                  </div>
                </div>
                <p className={`text-xl font-bold ${
                  drink.already_paid
                    ? 'text-info-600 dark:text-info-400'
                    : 'text-success-600 dark:text-success-400'
                }`}>
                  {formatCurrency(drink.amount.toString())}
                </p>
              </div>
            ))}
            {/* Total */}
            <div className="flex items-center justify-between pt-4 mt-2 border-t-2 border-gray-300 dark:border-gray-600">
              <p className="text-base font-bold text-gray-900 dark:text-white">
                GESAMTBETRAG:
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(
                  paidDrinks.reduce((sum, drink) => sum + drink.amount, 0).toString()
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {Object.keys(params).length > 0 && (
        <>
          {/* Transaction Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Status */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircleIcon className="h-5 w-5 text-success-600 dark:text-success-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Status</span>
              </div>
              <p className="text-xl font-bold text-success-600 dark:text-success-400 text-right sm:text-left">
                {params.st || params.payment_status || 'Completed'}
              </p>
            </div>

            {/* Amount */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center gap-3 mb-2">
                <BanknotesIcon className="h-5 w-5 text-success-600 dark:text-success-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Betrag</span>
              </div>
              <p className="text-xl font-bold text-success-600 dark:text-success-400 text-right sm:text-left">
                {params.amt || params.mc_gross ? formatCurrency(params.amt || params.mc_gross, params.cc || params.mc_currency) : '-'}
              </p>
              {params.mc_fee && (
                <p className="text-xs text-warning-600 dark:text-warning-400 mt-1 font-semibold text-right sm:text-left">
                  Gebühr: {formatCurrency(params.mc_fee, params.mc_currency)}
                </p>
              )}
            </div>

            {/* Transaction ID */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center gap-3 mb-2">
                <HashtagIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Transaktions-ID</span>
              </div>
              <p className="text-sm font-mono text-gray-900 dark:text-white break-all text-right sm:text-left">
                {params.tx || params.txn_id || '-'}
              </p>
            </div>
          </div>

          {/* Buyer Information */}
          {(params.payer_email || params.first_name) && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <UserIcon className="h-6 w-6 text-primary-600" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Käufer-Information
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {params.first_name && (
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <UserIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Name:</span>
                    </div>
                    <p className="text-gray-900 dark:text-white font-medium text-right sm:text-left">
                      {params.first_name} {params.last_name}
                    </p>
                  </div>
                )}
                {params.payer_email && (
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <EnvelopeIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">E-Mail:</span>
                    </div>
                    <p className="text-gray-900 dark:text-white font-medium break-all text-right sm:text-left">
                      {params.payer_email}
                    </p>
                  </div>
                )}
                {params.payer_status && (
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <CheckBadgeIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Status:</span>
                    </div>
                    <p className="text-gray-900 dark:text-white font-medium flex items-center gap-2 justify-end sm:justify-start">
                      {params.payer_status === 'VERIFIED' && (
                        <CheckCircleIcon className="h-4 w-4 text-success-600" />
                      )}
                      {params.payer_status}
                    </p>
                  </div>
                )}
                {params.payer_id && (
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <IdentificationIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">PayPal-ID:</span>
                    </div>
                    <p className="text-gray-900 dark:text-white font-medium font-mono text-sm text-right sm:text-left">
                      {params.payer_id}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payment Details */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <CreditCardIcon className="h-6 w-6 text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Zahlungsdetails
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {params.item_name && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <ShoppingBagIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Artikel:</span>
                  </div>
                  <p className="text-gray-900 dark:text-white font-medium text-right sm:text-left">
                    {params.item_name}
                  </p>
                </div>
              )}
              {params.payment_date && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <CalendarIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Datum:</span>
                  </div>
                  <p className="text-gray-900 dark:text-white font-medium text-right sm:text-left">
                    {formatDate(params.payment_date)}
                  </p>
                </div>
              )}
              {params.payment_type && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <CreditCardIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Zahlungsart:</span>
                  </div>
                  <p className="text-gray-900 dark:text-white font-medium capitalize text-right sm:text-left">
                    {params.payment_type}
                  </p>
                </div>
              )}
              {params.custom && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <HashtagIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Referenz:</span>
                  </div>
                  <p className="text-gray-900 dark:text-white font-medium font-mono text-sm break-all text-right sm:text-left">
                    {params.custom}
                  </p>
                </div>
              )}
              {params.protection_eligibility && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldCheckIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Käuferschutz:</span>
                  </div>
                  <p className="text-gray-900 dark:text-white font-medium flex items-center gap-2 justify-end sm:justify-start">
                    <CheckCircleIcon className="h-4 w-4 text-success-600" />
                    {params.protection_eligibility}
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Back Button */}
      <button
        onClick={() => navigate('/my-drinks')}
        className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
      >
        <ArrowLeftIcon className="h-5 w-5" />
        Zurück zu Meine Verzehrkosten
      </button>
    </div>
  );
};

export default PayPalSuccess;
